import type { ProductionFittingComment } from "./generated/shared/types/production_fitting_comment.js";
/**
 * schema.ts — Yjs shared document types for a Tech Pack
 *
 * Every field follows the same envelope shape so the collaborative
 * layer never needs to know about translations or user identity:
 *
 *   {
 *     value:          string | number,   // the actual field value
 *     language_code:  string,            // BCP-47, e.g. "en", "zh-CN"
 *     last_edited_by: string,            // user UUID
 *     timestamp:      number,            // Unix ms
 *   }
 *
 * Colorways use a flat composite-key structure in Y.Map<string>:
 *   Keys: {uuid}.name  {uuid}.hex  {uuid}.fabric
 *         {uuid}.version  {uuid}.status
 *   status: 'active' | 'archived' (soft delete only — never hard delete)
 *   version: integer string, incremented on each edit
 */

import * as Y from "yjs";
import { v4 as uuidv4 } from "uuid";

// ---------------------------------------------------------------------------
// Field envelope
// ---------------------------------------------------------------------------

export interface FieldEnvelope {
  value: string | number;
  language_code: string;
  last_edited_by: string;
  timestamp: number;
}

export function makeField(
  value: string | number,
  language_code: string,
  last_edited_by: string,
): FieldEnvelope {
  return { value, language_code, last_edited_by, timestamp: Date.now() };
}

// ---------------------------------------------------------------------------
// Section type aliases
// ---------------------------------------------------------------------------

// Canonical metadata field set. `style_number` is the Tech Pack's business
// identifier (P0-A): the single source of truth lives here as
// metadata.style_number.value; tech_packs.style_number is only a derived
// write-through projection. Every metadata field is declared so field-key
// bindings are compile-checked and content can never be routed into the wrong
// field (e.g. Style Number bleeding into Product Category).
export interface MetadataSection {
  style_number: FieldEnvelope;
  product_category?: FieldEnvelope;
  project_name?: FieldEnvelope;
  season: FieldEnvelope;
  version?: FieldEnvelope;
  description?: FieldEnvelope;
  size_range?: FieldEnvelope;
  size_system?: FieldEnvelope;
  _doc_status?: FieldEnvelope;
  date: FieldEnvelope;
  created_by: FieldEnvelope;
}

export interface MaterialsSection {
  fabric: FieldEnvelope;
  trim: FieldEnvelope;
  accessories: FieldEnvelope;
}

export type ConstructionStepsSection = FieldEnvelope[];

export interface MeasurementsSection {
  [key: string]: FieldEnvelope;
}

// ---------------------------------------------------------------------------
// Row shapes for Y.Array tables
// ---------------------------------------------------------------------------

/**
 * A materials row -- used identically for `bom`, `interlining` and `trim`.
 *
 * Shape follows the canonical materials row in
 * governance/product/CANONICAL_TECH_PACK_VERSION_CONTRACT.md §1, mirrored in
 * shared/types/canonical_techpack_fields.ts (CANONICAL_MATERIAL_COLUMNS) and
 * specflow/core/canonical_techpack.py (MaterialRow, authoritative).
 *
 * Every field is `string` because Yjs stores these as plain text cells;
 * numeric coercion happens at the canonical boundary. Empty string means
 * UNSET -- this contract never fabricates a price, cost, vendor or
 * consumption figure that wasn't explicitly entered.
 *
 * CODE VOCABULARIES (contract §1): `fabric_code`/`color_code` are
 * PRODUCTION/VENDOR codes ("T-4821", "19-4052 TCX"), freely editable per row.
 * They are NOT the style number's 2-digit `fabric_class_code`/
 * `color_class_code`, which are code_registry CLASSIFICATION values baked
 * into the immutable 9-segment identity and changeable only via Fork -> new
 * UUID. Never map one onto the other.
 */
export interface BomRow {
  id: string;
  component: string;
  material: string;
  composition: string;
  gsm: string;
  color_code: string;
  fabric_code: string;
  width: string;
  specification?: string;
  /**
   * @deprecated Superseded by `unit_price`, which names the quantity
   * unambiguously (Studio's bare "price" was ambiguous between unit cost and
   * line cost). Retained and mirrored on write so documents saved before the
   * canonical alignment keep rendering; readers should prefer `unit_price`
   * and fall back to this.
   */
  price: string;
  // --- canonical sourcing / costing fields (contract §1) ---
  vendor_article_no?: string;
  unit_price?: string;
  currency?: string;
  consumption_qty?: string;
  consumption_unit?: string;
  /** Derived: unit_price x consumption_qty. Blank unless BOTH are present --
   *  never partially estimated. Stored rather than recomputed on read so a
   *  locked Version's figure cannot drift later. */
  estimated_line_cost?: string;
  special_process_notes?: string;
}

export interface PatternPieceRow {
  id: string;
  name: string;
  qty: string;
  notes: string;
}

export interface ConstructionInstructionRow {
  id: string;
  piece: string;
  stitch_type: string;
  seam_finish: string;
  spi_thread: string;
  notes: string;
}

// ---------------------------------------------------------------------------
// Colorway helpers — flat composite keys in Y.Map<string>
//
// Key format: {uuid}.{field}
//   {uuid}.name     — display name
//   {uuid}.hex      — hex colour string e.g. "#cc4400"
//   {uuid}.fabric   — fabric reference string
//   {uuid}.version  — integer (as string) incremented on each edit
//   {uuid}.status   — "active" | "archived"
// ---------------------------------------------------------------------------

export interface ColorwayRecord {
  id: string;
  name: string;
  hex: string;
  fabric: string;
  version: number;
}

/** Add a new colorway. Returns the new UUID. */
export function addColorway(
  doc: TechPackDoc,
  data: { name: string; hex: string; fabric: string },
): string {
  const id = uuidv4();
  doc.ydoc.transact(() => {
    doc.colorways.set(`${id}.name`, data.name);
    doc.colorways.set(`${id}.hex`, data.hex);
    doc.colorways.set(`${id}.fabric`, data.fabric);
    doc.colorways.set(`${id}.version`, "1");
    doc.colorways.set(`${id}.status`, "active");
  });
  return id;
}

/** Update a single field on an existing colorway. Increments version. */
export function updateColorway(
  doc: TechPackDoc,
  id: string,
  field: "name" | "hex" | "fabric",
  value: string,
): void {
  doc.ydoc.transact(() => {
    doc.colorways.set(`${id}.${field}`, value);
    const prev = parseInt(doc.colorways.get(`${id}.version`) ?? "0", 10);
    doc.colorways.set(`${id}.version`, String(prev + 1));
  });
}

/**
 * Soft-delete a colorway by setting its status to 'archived'.
 * Never removes keys from Y.Map — CRDT safety.
 */
export function archiveColorway(doc: TechPackDoc, id: string): void {
  doc.ydoc.transact(() => {
    doc.colorways.set(`${id}.status`, "archived");
  });
}

/** Return only active colorways, sorted by name. */
export function getActiveColorways(doc: TechPackDoc): ColorwayRecord[] {
  const map = doc.colorways;
  const ids = new Set<string>();

  for (const key of map.keys()) {
    const dot = key.indexOf(".");
    if (dot !== -1) ids.add(key.slice(0, dot));
  }

  const result: ColorwayRecord[] = [];
  for (const id of ids) {
    const status = map.get(`${id}.status`) ?? "active";
    if (status === "archived") continue;
    result.push({
      id,
      name: map.get(`${id}.name`) ?? "",
      hex: map.get(`${id}.hex`) ?? "#cccccc",
      fabric: map.get(`${id}.fabric`) ?? "",
      version: parseInt(map.get(`${id}.version`) ?? "1", 10),
    });
  }

  return result.sort((a, b) => a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------
// TechPackDoc — wrapper around a Y.Doc with typed accessors
// ---------------------------------------------------------------------------

export class TechPackDoc {
  readonly ydoc: Y.Doc;

  // E-1 v2 hydration flags (plain JS, NOT Yjs — never synced/persisted).
  // Set once by StudioContext when the initial REST/DB load settles:
  //   hydrated — load attempt finished; the form may now render (gates the
  //              self-seeders so they can't pre-empt the load).
  //   loadOk   — the load actually succeeded; false on failure/timeout →
  //              degraded read-only: App.tsx autoSave refuses to persist over
  //              unknown DB state. Both default false until the load settles.
  hydrated = false;
  loadOk = false;

  // Original fields
  readonly metadata: Y.Map<FieldEnvelope>;
  readonly materials: Y.Map<FieldEnvelope>;
  readonly construction_steps: Y.Array<FieldEnvelope>;
  readonly measurements: Y.Map<FieldEnvelope>;
  /** Flat composite-key map — use colorway helper functions, not this directly */
  readonly colorways: Y.Map<string>;

  // Extended fields
  readonly bom: Y.Array<BomRow>;
  readonly interlining: Y.Array<BomRow>;
  readonly trim: Y.Array<BomRow>;
  readonly production_fitting_comments: Y.Array<ProductionFittingComment>;
  readonly pattern_pieces: Y.Array<PatternPieceRow>;
  readonly construction_instructions: Y.Array<ConstructionInstructionRow>;
  readonly style_id: Y.Map<FieldEnvelope>;
  readonly construction_details: Y.Map<FieldEnvelope>;
  readonly images: Y.Map<string>;

  constructor(ydoc: Y.Doc = new Y.Doc()) {
    this.ydoc = ydoc;

    this.metadata = ydoc.getMap<FieldEnvelope>("metadata");
    this.materials = ydoc.getMap<FieldEnvelope>("materials");
    this.construction_steps = ydoc.getArray<FieldEnvelope>("construction_steps");
    this.measurements = ydoc.getMap<FieldEnvelope>("measurements");
    this.colorways = ydoc.getMap<string>("colorways");

    this.bom = ydoc.getArray<BomRow>("bom");
    this.interlining = ydoc.getArray<BomRow>("interlining");
    this.trim = ydoc.getArray<BomRow>("trim");
    this.production_fitting_comments = ydoc.getArray<ProductionFittingComment>("production_fitting_comments");
    this.pattern_pieces = ydoc.getArray<PatternPieceRow>("pattern_pieces");
    this.construction_instructions = ydoc.getArray<ConstructionInstructionRow>("construction_instructions");
    this.style_id = ydoc.getMap<FieldEnvelope>("style_id");
    this.construction_details = ydoc.getMap<FieldEnvelope>("construction_details");
    this.images = ydoc.getMap<string>("images");
  }

  // ---------------------------------------------------------------------------
  // Convenience setters — always wrapped in a transaction
  // ---------------------------------------------------------------------------

  setMetadataField(
    key: keyof MetadataSection,
    value: string,
    language_code: string,
    user_id: string,
  ): void {
    this.ydoc.transact(() => {
      this.metadata.set(key, makeField(value, language_code, user_id));
    });
  }

  setMaterialField(
    key: keyof MaterialsSection,
    value: string,
    language_code: string,
    user_id: string,
  ): void {
    this.ydoc.transact(() => {
      this.materials.set(key, makeField(value, language_code, user_id));
    });
  }

  addConstructionStep(
    instruction: string,
    language_code: string,
    user_id: string,
  ): void {
    this.ydoc.transact(() => {
      this.construction_steps.push([
        makeField(instruction, language_code, user_id),
      ]);
    });
  }

  setMeasurement(
    key: string,
    value: number,
    language_code: string,
    user_id: string,
  ): void {
    this.ydoc.transact(() => {
      this.measurements.set(key, makeField(value, language_code, user_id));
    });
  }
}

// ---------------------------------------------------------------------------
// Canonical TP collection registry (G1)
//
// ONE source of truth for which Yjs collections make up a Tech Pack and how they
// map to the doc_json / Save-payload shape. Both directions are derived from this
// list so the save serializer and the reload hydrator can NEVER drift apart
// (the bug that lost trim / style_id / pattern_pieces / construction_instructions /
// interlining: they were saved but not re-hydrated, or — for trim — never saved).
//
//   payloadKey — the key in doc_json / the PATCH payload / the GET response.
//   prop       — the property on TechPackDoc (a Y.Map or Y.Array).
//   kind       — "map"   → Y.Map<FieldEnvelope|string>, serialized as an object.
//                "array" → Y.Array<row>, serialized as an array.
//
// NOTE payloadKey === prop for every collection EXCEPT construction_details,
// whose payload key is the legacy "construction" — preserved so existing stored
// docs keep loading.
// ---------------------------------------------------------------------------

export interface TpCollectionSpec {
  payloadKey: string;
  prop: "metadata" | "measurements" | "construction_details" | "style_id" | "images"
      | "production_fitting_comments" | "bom" | "interlining" | "trim" | "pattern_pieces" | "construction_instructions";
  kind: "map" | "array";
}

export const TP_COLLECTIONS: TpCollectionSpec[] = [
  { payloadKey: "production_fitting_comments", prop: "production_fitting_comments", kind: "array" },
  { payloadKey: "metadata",                  prop: "metadata",                  kind: "map"   },
  { payloadKey: "measurements",              prop: "measurements",              kind: "map"   },
  { payloadKey: "construction",              prop: "construction_details",      kind: "map"   },
  { payloadKey: "style_id",                  prop: "style_id",                  kind: "map"   },
  { payloadKey: "images",                    prop: "images",                    kind: "map"   },
  { payloadKey: "bom",                       prop: "bom",                       kind: "array" },
  { payloadKey: "interlining",               prop: "interlining",               kind: "array" },
  { payloadKey: "trim",                      prop: "trim",                      kind: "array" },
  { payloadKey: "pattern_pieces",            prop: "pattern_pieces",            kind: "array" },
  { payloadKey: "construction_instructions", prop: "construction_instructions", kind: "array" },
];

// Serialize EVERY canonical collection into the doc_json / Save-payload shape.
// Used by both autosave and explicit Save so neither can omit a collection
// (this is what restores trim to the persisted payload).
export function serializeTechPackCollections(tp: TechPackDoc): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const c of TP_COLLECTIONS) {
    const y = tp[c.prop] as Y.Map<unknown> | Y.Array<unknown>;
    out[c.payloadKey] = c.kind === "map"
      ? Object.fromEntries((y as Y.Map<unknown>).entries())
      : (y as Y.Array<unknown>).toArray();
  }
  return out;
}

// Hydrate EVERY canonical collection from a loaded doc_json on reopen. Collab-safe:
//   map   — seed a key only if absent; additionally, for a key whose LOCAL value
//           is empty while the SERVER value is non-empty, take the server value
//           (the identity-reconcile that stops a stale-empty envelope — e.g. a
//           blank style_number or size_range — from hiding the saved value). A
//           non-empty local edit is never clobbered.
//   array — push the server rows only when the local array is still empty (never
//           duplicate or overwrite a populated table).
// Must be called inside a ydoc.transact() by the caller.
export function hydrateTechPackCollections(tp: TechPackDoc, data: Record<string, unknown>): void {
  for (const c of TP_COLLECTIONS) {
    const incoming = data[c.payloadKey];
    if (c.kind === "map") {
      if (!incoming || typeof incoming !== "object") continue;
      const ymap = tp[c.prop] as Y.Map<unknown>;
      for (const [k, v] of Object.entries(incoming as Record<string, unknown>)) {
        if (!ymap.has(k)) { ymap.set(k, v); continue; }
        const localVal  = fieldText(ymap.get(k));
        const serverVal = fieldText(v);
        if (!localVal && serverVal) ymap.set(k, v);
      }
    } else {
      if (!Array.isArray(incoming) || incoming.length === 0) continue;
      const yarr = tp[c.prop] as Y.Array<unknown>;
      if (yarr.length === 0) yarr.push(incoming);
    }
  }
}

// Extract the comparable text of a map value, whether it is a FieldEnvelope
// ({value}) or a plain string (images map). Empty/whitespace → "".
function fieldText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "object" && "value" in (v as Record<string, unknown>)) {
    const inner = (v as { value?: unknown }).value;
    return inner === null || inner === undefined ? "" : String(inner).trim();
  }
  return "";
}
